import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, LogOut, PanelLeftClose, PanelLeftOpen, ChevronDown, User as UserIcon, Sun, Moon, Monitor, Smartphone, Languages, Check, BellRing, WifiOff, RefreshCw, Braces, BookOpen, FileJson, Waves } from 'lucide-react';
import { ws, type WsStatus } from '@/lib/ws';
import { useAuthStore } from '@/store/auth-store';
import { useUiStore } from '@/store/ui-store';
import { useThemeStore, type Theme } from '@/store/theme-store';
import { setUiMode } from '@/hooks/use-is-mobile';
import { useT, useLocaleStore, LOCALES, type Locale } from '@/lib/i18n';
import { appBrand } from '@/lib/branding';

// =============================================================================
// Topbar: sidebar toggle + judul halaman aktif (kiri) + bell + user dropdown
// (Profile/Logout di kanan). Switch user TIDAK di sini lagi — dipindah ke
// MonitoringPanel (dekat tabs Devices/POI/Geofences).
// =============================================================================

// pageTitle: map pathname → judul halaman (prefix match, longest wins).
const TITLES: Array<[string, string]> = [
  ['/dashboard', 'Dashboard'],
  ['/monitoring', 'Monitoring'],
  ['/playback', 'Playback'],
  ['/reports/trip', 'Trip Report'],
  ['/vehicles', 'Vehicles'],
  ['/users', 'Users'],
  ['/device-member', 'Device Member'],
  ['/maintenance/records', 'Service Record'],
  ['/maintenance', 'Maintenance'],
  ['/notification-pref', 'Notifikasi Alarm'],
  ['/whatsapp-setup', 'WhatsApp'],
  ['/tenants', 'Tenants'],
  ['/shares', 'Active Shares'],
  ['/rental/return', 'Rental Return'],
  ['/rental/report', 'Rental Report'],
  ['/rental/checklist', 'Checklist Master'],
  ['/rental', 'Rental'],
  ['/vehicle-cost', 'Vehicle Cost'],
  ['/invoice', 'Invoice'],
  ['/api-keys', 'API Keys'],
  ['/api-doc', 'Dokumentasi API'],
  ['/vehicle-brands', 'Vehicle Brands'],
  ['/gps-brands', 'GPS Brand & Model'],
  ['/platform-profile', 'Platform Profile'],
  ['/profile', 'Profil Saya'],
  ['/poi', 'POI'],
  ['/geofence', 'Geofence'],
];
function pageTitle(pathname: string): string {
  for (const [prefix, title] of TITLES) {
    if (pathname.startsWith(prefix)) return title;
  }
  return appBrand.name;
}

export function Topbar() {
  const claims = useAuthStore((s) => s.claims);
  const logout = useAuthStore((s) => s.logout);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const location = useLocation();

  const t = useT();
  return (
    <>
      <header className="h-14 bg-chrome text-chrome-foreground border-b border-border flex items-center justify-between px-4 gap-3 shadow-sm dark:shadow-none relative z-10">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="p-2 rounded-md hover:bg-accent"
          >
            {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>

          {/* Judul halaman aktif (menggantikan switch-user yang kini di MonitoringPanel). */}
          <h1 className="text-sm font-semibold tracking-tight">{pageTitle(location.pathname)}</h1>
        </div>

        <div className="flex items-center gap-2">
          <WsStatusBadge />
          <button
            type="button"
            onClick={() => setUiMode('mobile')}
            title="Tampilan mobile"
            aria-label="Mobile view"
            className="p-2 rounded-md hover:bg-accent text-muted-foreground"
          >
            <Smartphone className="w-4 h-4" />
          </button>
          <ThemeMenu />
          <LangMenu />
          <ApiMenu />
          <button
            type="button"
            aria-label={t('topbar.notifications')}
            title={t('topbar.notifications')}
            className="p-2 rounded-md hover:bg-accent"
          >
            <Bell className="w-4 h-4" />
          </button>

          <UserMenu name={claims?.user ?? '-'} onLogout={logout} />
        </div>
      </header>
    </>
  );
}

// User menu dropdown — nama user → Profile / Logout.
function UserMenu({ name, onLogout }: { name: string; onLogout: () => void }) {
  const navigate = useNavigate();
  const t = useT();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-accent text-sm"
        >
          <UserIcon className="w-4 h-4" />
          <span className="font-medium">{name}</span>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-[160px] bg-popover border border-border rounded-md shadow-lg overflow-hidden text-sm"
        >
          <DropdownMenu.Item
            onSelect={() => navigate('/profile')}
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent outline-none"
          >
            <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
            {t('topbar.profile')}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => navigate('/notification-pref')}
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent outline-none"
          >
            <BellRing className="w-3.5 h-3.5 text-muted-foreground" />
            Notifikasi Alarm
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="h-px bg-border" />
          <DropdownMenu.Item
            onSelect={onLogout}
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-destructive/10 text-destructive outline-none"
          >
            <LogOut className="w-3.5 h-3.5" />
            {t('topbar.logout')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// Theme dropdown — light / dark / system. Icon ikut state aktif.
function ThemeMenu() {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const t = useT();
  const options: Array<{ value: Theme; label: string; icon: typeof Sun }> = [
    { value: 'light',  label: t('topbar.theme.light'),  icon: Sun },
    { value: 'dark',   label: t('topbar.theme.dark'),   icon: Moon },
    { value: 'navy',   label: 'Blue Navy',              icon: Waves },
    { value: 'system', label: t('topbar.theme.system'), icon: Monitor },
  ];
  const active = options.find((o) => o.value === theme) ?? options[options.length - 1];
  const ActiveIcon = active.icon;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={t('topbar.theme')}
          className="p-2 rounded-md hover:bg-accent"
        >
          <ActiveIcon className="w-4 h-4" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end" sideOffset={4}
          className="z-50 min-w-[140px] bg-popover border border-border rounded-md shadow-lg overflow-hidden text-sm"
        >
          {options.map((opt) => {
            const Icon = opt.icon;
            return (
              <DropdownMenu.Item
                key={opt.value}
                onSelect={() => setTheme(opt.value)}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent outline-none"
              >
                <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="flex-1">{opt.label}</span>
                {theme === opt.value && <Check className="w-3.5 h-3.5 text-primary" />}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// API dropdown — link dokumentasi API (Swagger UI + spec OpenAPI JSON) yang
// di-serve api-server di /swagger/* (di-proxy nginx prod & vite dev).
function ApiMenu() {
  const navigate = useNavigate();
  const base = import.meta.env.VITE_API_BASE ?? '';
  const open = (path: string) => window.open(base + path, '_blank', 'noopener');
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title="API (dokumentasi)"
          aria-label="Dokumentasi API"
          className="p-2 rounded-md hover:bg-accent flex items-center gap-1"
        >
          <Braces className="w-4 h-4" />
          <span className="text-[10px] uppercase font-bold text-muted-foreground">API</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end" sideOffset={4}
          className="z-50 min-w-[190px] bg-popover border border-border rounded-md shadow-lg overflow-hidden text-sm"
        >
          <DropdownMenu.Item
            onSelect={() => navigate('/api-doc')}
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent outline-none"
          >
            <BookOpen className="w-3.5 h-3.5 text-muted-foreground" />
            Dokumentasi API
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => open('/swagger/index.html')}
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent outline-none"
          >
            <Braces className="w-3.5 h-3.5 text-muted-foreground" />
            Live Test (Swagger UI)
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => open('/swagger/doc.json')}
            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent outline-none"
          >
            <FileJson className="w-3.5 h-3.5 text-muted-foreground" />
            OpenAPI Spec (JSON)
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// Language dropdown — Indonesia / English.
function LangMenu() {
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);
  const t = useT();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={t('topbar.language')}
          className="p-2 rounded-md hover:bg-accent flex items-center gap-1"
        >
          <Languages className="w-4 h-4" />
          <span className="text-[10px] uppercase font-bold text-muted-foreground">{locale}</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end" sideOffset={4}
          className="z-50 min-w-[140px] bg-popover border border-border rounded-md shadow-lg overflow-hidden text-sm"
        >
          {LOCALES.map((opt) => (
            <DropdownMenu.Item
              key={opt.value}
              onSelect={() => setLocale(opt.value as Locale)}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent outline-none"
            >
              <span className="text-base">{opt.flag}</span>
              <span className="flex-1">{opt.label}</span>
              {locale === opt.value && <Check className="w-3.5 h-3.5 text-primary" />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// =============================================================================
// WsStatusBadge — indicator koneksi realtime. Tampil di topbar.
//   - open          : Wifi hijau (hidden, agar tidak distraktif)
//   - connecting    : RefreshCw spinning amber
//   - reconnecting  : RefreshCw spinning amber + tooltip
//   - closed / idle : WifiOff merah + tooltip "Tidak terhubung"
// =============================================================================
function WsStatusBadge() {
  const [status, setStatus] = useState<WsStatus>(ws.getStatus());

  useEffect(() => ws.onStatusChange(setStatus), []);

  // Hide saat normal (open) — tidak distraktif. Tampil cuma saat anomali.
  if (status === 'open') return null;

  if (status === 'connecting' || status === 'reconnecting') {
    return (
      <span
        title={status === 'reconnecting'
          ? 'Realtime terputus, mencoba menyambung ulang…'
          : 'Menyambungkan ke server…'}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700"
      >
        <RefreshCw className="w-3 h-3 animate-spin" />
        <span className="hidden sm:inline">
          {status === 'reconnecting' ? 'Reconnecting…' : 'Connecting…'}
        </span>
      </span>
    );
  }
  // closed / idle
  return (
    <span
      title="Realtime tidak terhubung. Data tidak update sampai koneksi pulih."
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] bg-red-100 text-red-800 border border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700"
    >
      <WifiOff className="w-3 h-3" />
      <span className="hidden sm:inline">Offline</span>
    </span>
  );
}
